import { Box, CircularProgress, Typography } from '@deere/fuel-react';
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BidForm } from '../../components/BidForm';
import { AdminContext } from '../../context/AdminContext';
import { AlertBox } from '../../components/AlertBox';
import { DeleteButton } from '../../components/DeleteButton';
import styles from './Page.module.css';
import { LoggedInContext } from '../../context/LoggedInContext';
import { errorBannerFromResponse } from '../../utils/errorBanner';

export const EditBid = () => {
    const { jobNumber } = useParams();
    const { selectedAdminGroup, error: contextError } =
        useContext(AdminContext);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingClassification, setIsLoadingClassification] =
        useState(true);
    const [submitError, setSubmitError] = useState(false);
    const [job, setJob] = useState();
    const [alertMsg, setAlertMsg] = useState('');
    const [classificationList, setClassificationList] = useState([]);
    const { makeRequest, authState } = useContext(LoggedInContext);

    const fetchJob = useCallback(() => {
        return makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/jobs/${jobNumber}`
        );
    }, [makeRequest, jobNumber]);

    useEffect(() => {
        let ignore = false;
        const fetchJobById = async () => {
            if (!ignore) {
                setIsLoading(true);
            }
            const response = await fetchJob();
            if (!ignore) {
                if (response.status === 200) {
                    const jobToEdit = await response.json();
                    setJob(jobToEdit);
                    setIsLoading(false);
                } else {
                    setSubmitError(true);
                    setIsLoading(false);
                    if (response.status === 404) {
                        setAlertMsg('The Job number does not exist.');
                    } else {
                        setAlertMsg(
                            errorBannerFromResponse(
                                response,
                                'Your job bid could not be loaded. Please try again later.'
                            ).message
                        );
                    }
                }
            }
        };
        fetchJobById();
        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [fetchJob]);

    useEffect(() => {
        let ignore = false;
        const fetchClassification = async () => {
            setIsLoadingClassification(true);
            try {
                const response = await makeRequest(
                    `${process.env.REACT_APP_API_ENDPOINT}/classification/${selectedAdminGroup.adGroup}`
                );

                if (!ignore) {
                    if (response.status === 200) {
                        const responseData = await response.json();
                        setClassificationList(responseData);
                        setIsLoadingClassification(false);
                    } else {
                        setIsLoadingClassification(false);
                        setSubmitError(true);
                        setAlertMsg(
                            errorBannerFromResponse(
                                response,
                                'We are unable to load classifications. Please try again later.'
                            ).message
                        );
                    }
                }
            } catch (err) {
                setSubmitError(true);
                setAlertMsg(
                    errorBannerFromResponse(
                        err,
                        'We are unable to load classifications. Please try again later.'
                    ).message
                );
            }
        };
        fetchClassification();
        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [authState, makeRequest, selectedAdminGroup]);

    const updateBid = async (updatedBid, isValid) => {
        const requestOptions = {
            method: 'POST',
            body: JSON.stringify(updatedBid),
        };
        if (!isValid) {
            setAlertMsg('You must fill out all required fields to edit bid.');
            setSubmitError(true);
            window.scrollTo(0, 0);
        } else {
            setSubmitError(false);
            const response = await makeRequest(
                `${process.env.REACT_APP_API_ENDPOINT}/jobs`,
                requestOptions
            );
            if (response.ok) {
                navigate('/pending-jobs', {
                    state: {
                        variant: 'success',
                        message: `You have successfully edited Job Bid! ${job.id}`,
                    },
                });
            } else {
                window.scrollTo(0, 0);
                setSubmitError(true);
                setAlertMsg(
                    errorBannerFromResponse(
                        response,
                        'Your job bid was not saved. Please try again later.'
                    ).message
                );
            }
        }
    };

    const deletePendingJobApiCall = async () => {
        const response = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/deletePendingJob/${job.id}`
        );

        if (response.ok) {
            navigate('/pending-jobs', {
                state: {
                    variant: 'success',
                    message: `Job Number ${job.id} was deleted`,
                },
            });
        } else {
            window.scrollTo(0, 0);
            setSubmitError(true);
            setAlertMsg(
                errorBannerFromResponse(
                    response,
                    'We are unable to delete this job bid. Please try again later.'
                ).message
            );
        }
    };

    return (
        <div className={styles.parentContainer}>
            <Box spacing={6} className={styles.contentToContainer}>
                <Box
                    className={styles.headerContainer}
                    display="block"
                    bgcolor="background.default"
                >
                    {submitError && (
                        <AlertBox msg={alertMsg} variant={'error'} />
                    )}
                    {contextError && (
                        <AlertBox
                            msg={
                                errorBannerFromResponse(
                                    contextError,
                                    'We are experiencing technical issues. Please try again later.'
                                ).message
                            }
                            variant={'error'}
                        />
                    )}
                    <Typography variant="h1" className={styles.heading}>
                        Job # {jobNumber}
                    </Typography>
                </Box>
                <Box className={styles.required}>
                    <span>* </span>
                    Required fields.
                </Box>
                {(isLoading || isLoadingClassification) && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}
                {!isLoading && !isLoadingClassification && !submitError && (
                    <BidForm
                        bidObj={job}
                        updateBid={updateBid}
                        deleteButt={
                            <DeleteButton deleteBid={deletePendingJobApiCall} />
                        }
                        classificationList={classificationList}
                    />
                )}
            </Box>
        </div>
    );
};
